import React, { useEffect, useState } from "react";
import { Row, Col, Card, CardBody, Button, Container } from "reactstrap";
import CourseService from "../../../services/KangarooServices/CourseService";
import { useNavigate, useParams } from "react-router-dom";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { Edit3 } from "lucide-react";
import {
  formatDate,
  formatDateTime,
} from "../../../Components/Common/FormatDate";

const CourseDetail = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const navigate = useNavigate();
  const fetchCourseDetail = async (id) => {
    try {
      setLoading(true);
      const response = await CourseService.getDetail(id);
      setData(response);
    } catch (err) {
      console.error("Failed to fetch course detail:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchCourseDetail(id);
    }
  }, [id]);


  return (
    <>
      <Container fluid>
        <div className="page-content">
          <BreadCrumb
            title="Course Detail"
            pageTitle="Courses"
            breadcrumbItems={[{ title: "Back to List", link: "/courses" }]}
          />
          {loading ? (
            <p>Loading...</p>
          ) : (
            <div>
              <Row className="gy-4">
                {/* Left: Image / Placeholder */}

                {/* Right: Details */}

                <Col lg={12}>
                  <Card className="shadow-sm border-0 overflow-hidden">
                    <div
                      className="bg-primary bg-gradient"
                      style={{ height: "6px" }}
                    ></div>
                    <CardBody className="p-4">
                      <div className="d-flex align-items-center mb-4">
                        <div
                          className="position-absolute mb-1 me-3"
                          style={{ right: "0", zIndex: 100 }}
                        >
                          <button
                            type="button"
                            className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                            onClick={() => navigate(`/course/create/${id}`)}
                            style={{ fontSize: "0.875rem" }}
                          >
                            <Edit3 size={14} />
                            Update
                          </button>
                        </div>
                        <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                          <i
                            className="bx bxs-graduation text-primary"
                            style={{ fontSize: "24px" }}
                          ></i>
                        </div>
                        <h5 className="mb-0 fw-semibold">Course Information</h5>
                      </div>

                      <Row>
                        <Col md={6}>
                          <div className="mb-4">
                            <label className="text-muted small mb-1 d-flex align-items-center">
                              <i className="bx bx-book-open me-1"></i> Course
                              Name
                            </label>
                            <p className="fw-semibold mb-0">{data.name}</p>
                          </div>
                        </Col>

                        <Col md={6}>
                          <div className="mb-4">
                            <label className="text-muted small mb-1 d-flex align-items-center">
                              <i className="bx bx-layer me-1"></i> Level
                            </label>
                            <p className="fw-semibold mb-0">
                              <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2">
                                {data.level}
                              </span>
                            </p>
                          </div>
                        </Col>

                        <Col md={6}>
                          <div className="mb-4">
                            <label className="text-muted small mb-1 d-flex align-items-center">
                              <i className="bx bx-time-five me-1"></i> Duration
                              (Months)
                            </label>
                            <p className="fw-semibold mb-0">
                              {data.durationMonths}
                            </p>
                          </div>
                        </Col>

                        <Col md={6}>
                          <div className="mb-4">
                            <label className="text-muted small mb-1 d-flex align-items-center">
                              <i className="bx bx-building me-1"></i> University
                            </label>
                            <p className="fw-semibold mb-0">
                              {data.university?.name}
                              <span className="text-muted ms-2">
                                ({data.university?.city})
                              </span>
                            </p>
                          </div>
                        </Col>

                        <Col md={6}>
                          <div className="mb-4">
                            <label className="text-muted small mb-1 d-flex align-items-center">
                              <i className="bx bx-dollar-circle me-1"></i>{" "}
                              Tuition From
                            </label>
                            <p className="fw-semibold mb-0 text-success">
                              {formatDate(data.tuitionFrom)}
                            </p>
                          </div>
                        </Col>

                        <Col md={6}>
                          <div className="mb-4">
                            <label className="text-muted small mb-1 d-flex align-items-center">
                              <i className="bx bx-dollar-circle me-1"></i>{" "}
                              Tuition To
                            </label>
                            <p className="fw-semibold mb-0 text-success">
                              {formatDate(data.tuitionTo)}
                            </p>
                          </div>
                        </Col>
                      </Row>

                      <div className="mb-4">
                        <label className="text-muted small mb-1 d-flex align-items-center">
                          <i className="bx bx-detail me-1"></i> Description
                        </label>
                        <div className="bg-light rounded p-3">
                          <p className="fw-semibold mb-0">{data.description}</p>
                        </div>
                      </div>

                      <Row className="mb-4">
                        <Col md={6}>
                          <div className="bg-light rounded p-3">
                            <label className="text-muted small mb-1 d-flex align-items-center">
                              <i className="bx bx-calendar-plus me-1"></i>{" "}
                              Created At
                            </label>
                            <p className="fw-semibold mb-0 small">
                              {new Date(data.createdAt).toLocaleString()}
                            </p>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="bg-light rounded p-3">
                            <label className="text-muted small mb-1 d-flex align-items-center">
                              <i className="bx bx-calendar-edit me-1"></i>{" "}
                              Updated At
                            </label>
                            <p className="fw-semibold mb-0 small">
                              {data.updatedAt
                                ? new Date(data.updatedAt).toLocaleString()
                                : "Not updated yet"}
                            </p>
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </div>
          )}
        </div>
      </Container>
    </>
  );
};

export default CourseDetail;
